import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Sparkles } from 'lucide-react';
import { useUIStore } from '@/contexts/uiStore';
import PricingModal from './PricingModal';

/**
 * Global upgrade modal that renders when upgradeModal.isOpen is true.
 * Uses the current project from URL params.
 * Shows a limit message first, then allows viewing plans.
 */
export default function GlobalUpgradeModal() {
  const { t } = useTranslation();
  const location = useLocation();
  // Extrai projectId de URLs como /dashboard/project/:projectId/...
  const projectIdMatch = location.pathname.match(/\/dashboard\/project\/([^/]+)/);
  const projectId = projectIdMatch?.[1];
  const { upgradeModal, closeUpgradeModal } = useUIStore();
  const [showPricing, setShowPricing] = useState(false);

  if (!upgradeModal.isOpen) return null;

  // If showing pricing modal
  if (showPricing && projectId) {
    return (
      <PricingModal
        isOpen={true}
        onClose={() => {
          setShowPricing(false);
          closeUpgradeModal();
        }}
        projectId={projectId}
      />
    );
  }

  // Limit reached message modal
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeUpgradeModal}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={closeUpgradeModal}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary-pink flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>

        <h2 className="text-xl font-bold text-[#1E293B] mb-2">
          {t('upgrade.limitReached', 'Limite atingido')}
        </h2>
        <p className="text-[#64748B] mb-4">{upgradeModal.reason}</p>

        {upgradeModal.currentUsage !== null && upgradeModal.limit !== null && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <span className="text-amber-700 font-medium">
              {t('upgrade.usageInfo', 'Uso: {{current}}/{{limit}}', {
                current: upgradeModal.currentUsage,
                limit: upgradeModal.limit,
              })}
            </span>
            {upgradeModal.tier && (
              <span className="ml-2 text-amber-600 text-sm capitalize">
                ({upgradeModal.tier})
              </span>
            )}
          </div>
        )}

        <p className="text-sm text-[#64748B] mb-6">
          {t(
            'upgrade.upgradeToUnlock',
            'Faça upgrade para desbloquear mais recursos e continuar usando.'
          )}
        </p>

        <div className="flex gap-3">
          <button
            onClick={closeUpgradeModal}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-[#64748B] rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          {projectId ? (
            <button
              onClick={() => setShowPricing(true)}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary to-secondary-pink text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {t('upgrade.seePlans', 'Ver planos')}
            </button>
          ) : (
            <p className="flex-1 text-sm text-[#94A3B8] flex items-center justify-center">
              {t(
                'upgrade.navigateToProject',
                'Navegue até um projeto para ver os planos.'
              )}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
